<template>
  <div class="d-flex flex-row align-start pb-2">
    <slot name="before-filter" />
    <filter-selection
      :key="firstLoad"
      :first-load="firstLoad"
      :filters="selectedFilters"
      :filter-values="filterValues"
      :permanent-list="permanentList"
      :loading="loading"
      :auto-reload="autoReload"
      @remove-filter="onRemoveFilter"
      @click="localDialog = true"
      @refresh="refresh"
    >
      <template v-if="withCustomFilters" #filter-list>
        <filter-list
          :favorite-id="favoriteFilterId"
          :filters="userFilters"
          :filter-id.sync="selectedUserFilterId"
          @update:favorite-id="saveFavoriteFilter"
          @select-user-filter="selectUserFilter"
          @delete-user-filter="deleteUserFilter"
        />
      </template>
      <template v-if="withCustomFilters" #save-filter>
        <save-filter @save="saveFilter" />
      </template>
    </filter-selection>
    <slot name="after-filter" />
    <v-dialog
      v-model="localDialog"
      width="784px"
      scrollable
      eager
      persistent
      @keydown.enter="onClickApply"
      @keydown.esc="localDialog = false"
    >
      <template #activator="{ on }">
        <div v-on="on" />
      </template>
      <template #default>
        <card class="filter-dialog-grid">
          <card-title large closeable @click:close="localDialog = false">
            {{ title || "Добавление фильтров" }}
          </card-title>
          <card-content :key="firstOpen">
            <validation-observer ref="validatorfilter">
              <div v-for="(group, groupKey) in schema" :key="groupKey">
                <div v-if="group.title" class="font-18 pb-2">
                  {{ group.title }}
                </div>
                <v-row dense>
                  <v-col
                    v-for="(filter, filterKey) in group.filters"
                    :key="filterKey"
                    v-bind="{ ...defaultColBind, ...filter.colBind }"
                  >
                    <component
                      :is="filter.component"
                      v-model="localFilters[filterKey]"
                      v-bind="filter.filterBind"
                      :label="filter.title || ''"
                      :output.sync="cachedFilterValues[filterKey]"
                      :permanent="permanentList[filterKey]"
                      @init="onInitFilter"
                      v-on="filter.filterOn"
                    />
                  </v-col>
                </v-row>
              </div>
            </validation-observer>
          </card-content>
          <card-actions>
            <v-spacer />
            <v-btn color="primary" outlined depressed @click="onClickReset">
              Сбросить
            </v-btn>
            <v-btn
              width="96px"
              color="primary"
              :disabled="!hasLocalFilters"
              depressed
              @click="onClickApply"
            >
              Найти
            </v-btn>
          </card-actions>
        </card>
      </template>
    </v-dialog>
  </div>
</template>
<script>
import FilterSelection from "./FilterSelection";
import UserFilters from "./UserFilters";
import FilterList from "./FilterList";
import SaveFilter from "./SaveFilter";
import Repository from "@/api/filter.js";
import { reduce, filter } from "lodash";
import NotificationMixin from "@/mixins/NotificationMixin";

export default {
  components: { FilterSelection, UserFilters, FilterList, SaveFilter },
  mixins: [NotificationMixin],
  model: { prop: "dialog", event: "changeDialog" },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    schema: {
      type: Object,
      default: () => ({})
    },
    permanentList: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: null
    },
    defaultColBind: {
      type: Object,
      default: () => ({ cols: 6, xs: 12 })
    },
    routeName: {
      type: String,
      default: null
    },
    withCustomFilters: {
      type: Boolean,
      default: false
    },
    autoReload: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      firstOpen: true,
      firstLoad: true,
      loading: false,
      isManageButtons: false,
      cachedFilterValues: {},
      filterValues: {},
      localFilters: {},
      userFilters: [],
      selectedUserFilterId: null,
      favoriteFilterId: null
    };
  },
  computed: {
    localRouteName() {
      return this.routeName || this.$route.name || "public";
    },
    localDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        if (!value && !this.isManageButtons) this.rollBackFilters();
        this.$emit("changeDialog", value);
      }
    },
    selectedFilters() {
      if (this.firstLoad) return [];
      return reduce(
        this.filters,
        (result, value, key) => {
          if (value) result.push(key);
          return result;
        },
        []
      );
    },
    hasLocalFilters() {
      return true; // filter(this.localFilters, (i) => !!i).length > 0;
    }
  },
  watch: {
    localDialog() {
      if (this.firstOpen) {
        this.$nextTick(() => {
          this.firstOpen = false;
        });
      }
    },
    filters: {
      handler(v) {
        this.localFilters = { ...v };
        this.$nextTick(() => {
          this.filterValues = { ...this.cachedFilterValues };
        });
      },
      deep: true
    }
  },
  async created() {
    if (this.withCustomFilters) {
      await this.loadUserFilters();
      const isImmediately = this.isImmediately(this.filters);
      if (this.favoriteFilterId && !isImmediately) this.setFavoriteFilter();
      else {
        this.localFilters = { ...this.filters };
        this.$emit("without-favorite");
      }
    } else {
      this.localFilters = { ...this.filters };
    }
    this.firstLoad = false;
    this.$nextTick(() => {
      this.filterValues = { ...this.cachedFilterValues };
    });
  },
  methods: {
    toggleDialog(toggle, isManage = false) {
      this.isManageButtons = isManage;
      this.localDialog = toggle;
      this.isManageButtons = false;
    },
    onClickReset() {
      this.resetFilters();
      this.toggleDialog(false, true);
    },
    async onClickApply() {
      if (!(await this.applyFilter())) return;
      this.toggleDialog(false, true);
    },
    async applyFilter() {
      if (!(await this.$refs.validatorfilter.validate())) {
        return false;
      }
      this.updateFilters();
      this.filterValues = { ...this.cachedFilterValues };
      this.$emit("apply", this.localFilters);
      return true;
    },
    onRemoveFilter(filterKey) {
      this.$delete(this.localFilters, filterKey);
      this.updateFilters();
      this.$emit("remove");
    },
    rollBackFilters() {
      this.localFilters = { ...this.filters };
      this.$emit("rollBack", this.localFilters);
    },
    resetFilters() {
      this.localFilters = reduce(
        this.filters,
        (result, filterValue, filterKey) => {
          if (this.permanentList[filterKey]) result[filterKey] = filterValue;
          return result;
        },
        {}
      );

      this.filterValues = reduce(
        this.filterValues,
        (result, filterValue, filterKey) => {
          if (this.permanentList[filterKey]) result[filterKey] = filterValue;
          return result;
        },
        {}
      );
      this.updateFilters();
      this.$emit("reset", this.localFilters);
    },
    updateFilters() {
      this.$emit("update:filters", { ...this.localFilters });
    },
    refresh() {
      this.$emit("refresh");
    },
    onInitFilter() {
      this.filterValues = { ...this.cachedFilterValues };
    },
    onClickExport() {
      this.onClickApply();
      this.$emit("export");
    },
    setFavoriteFilter() {
      const { userFilters, favoriteFilterId } = this;
      const { value, id } = userFilters.find(i => i.id === favoriteFilterId);
      this.localFilters = value;
      this.selectedUserFilterId = id;
      this.$nextTick(this.applyFilter);
    },
    async loadUserFilters() {
      this.loading = true;
      const { data } = await Repository.getFilters(this.localRouteName);
      this.loading = false;
      this.favoriteFilterId = data.favoriteId;
      this.userFilters = data.filters;
    },
    async saveFilter(name) {
      try {
        this.loading = true;
        const { localFilters: filters, filterValues } = this;
        const description = filter(filterValues, i => !!i).join(", ");
        const { data } = await Repository.createFilter({
          name,
          filters,
          description
        });
        this.sendAlert(data);
        this.loadUserFilters();
      } catch (error) {
        this.sendError(error);
      } finally {
        this.loading = false;
      }
    },
    selectUserFilter(id) {
      this.localFilters = this.userFilters.find(item => item.id == id).value;
      this.$nextTick(this.onClickApply);
    },
    async deleteUserFilter(id) {
      try {
        this.loading = true;
        const { data } = await Repository.deleteFilter(id);
        this.loadUserFilters();
        this.sendAlert(data);
      } catch (error) {
        this.sendError(error);
      } finally {
        this.loading = false;
      }
    },
    async saveFavoriteFilter(id) {
      try {
        this.loading = true;
        const payload = { id, route: this.routeName };
        await (id
          ? Repository.addFavorite(payload)
          : Repository.deleteFavorite(payload));
      } catch (error) {
        this.sendError(error);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Привет друг. Мне нужно скрытое (чтобы по нему не генерировались чипсы) поле в объекте filters
     * Для этого я буду проверять ключ immediately типа Symbol
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol
     */
    isImmediately(filters) {
      // Найти Symbol immediately
      const symbol = Object.getOwnPropertySymbols(filters).find(
        s => s.description === "immediately"
      );
      if (!symbol) return false;
      return filters[symbol] === true;
    }
  }
};
</script>

<style lang="scss">
.filter-dialog-grid {
  .v-input {
    .v-text-field__details {
      margin-bottom: 0px !important;
    }
  }
}
</style>
